import { useState, useEffect, createContext, useContext, ReactNode, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { toast } from '@/lib/toast';
import { useNavigate } from 'react-router-dom';
import { useCardStore } from '@/store/cardStore';
import { useDeckStore } from '@/store/deckStore';
import { useUIStore } from '@/store/uiStore';
import { Skeleton } from '@/components/ui/skeleton';

interface TermsMetadata {
	generalTermsVersion?: string;
}

interface AuthContextType {
	user: User | null;
	session: Session | null;
	loading: boolean;
	signUp: (email: string, password: string) => Promise<{ error: any }>;
	signIn: (email: string, password: string) => Promise<{ error: any }>;
	signInOrSignUp: (
		email: string,
		password: string,
		rememberMe?: boolean,
		termsMetadata?: TermsMetadata,
		intentRole?: 'buyer' | 'seller'
	) => Promise<{ error: any; didSignUp?: boolean }>;
	signInWithGoogle: () => Promise<{ error: any }>;
	signInWithApple: () => Promise<{ error: any }>;
	signInWithMagicLink: (
		email: string,
		termsMetadata?: TermsMetadata,
		intentRole?: 'buyer' | 'seller'
	) => Promise<{ error: any }>;
	signOut: () => Promise<void>;
	updatePassword: (newPassword: string) => Promise<{ error: any }>;
	hasPassword: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);
	const isInitialLoad = useRef(true);
	const hasShownWelcome = useRef(false);
	const navigate = useNavigate();

	useEffect(() => {
		supabase.auth.getSession().then(({ data: { session } }) => {
			setSession(session);
			setUser(session?.user ?? null);
			setLoading(false);
		});

		const {
			data: { subscription }
		} = supabase.auth.onAuthStateChange((event, session) => {
			setSession(session);
			setUser(session?.user ?? null);

			// Skip feedback on initial load
			if (isInitialLoad.current) {
				isInitialLoad.current = false;
				return;
			}

			// Provide feedback for auth state changes
			if (event === 'SIGNED_IN' && session?.user) {
				// Only show welcome toast for actual new sign-ins, not session restorations
				if (!hasShownWelcome.current) {
					hasShownWelcome.current = true;
					const displayName = session.user.user_metadata?.full_name || session.user.email;
					toast.success(`Welcome${displayName ? `, ${displayName}` : ''}!`);

					// First-login onboarding: gently suggest Collectr import (toast, not full sheet).
					// Suppress for anyone who already has inventory or has imported before — this
					// is a true first-time nudge, not a recurring reminder.
					if (typeof window !== 'undefined') {
						const promptedKey = `cardsimp:onboardImportPrompted:${session.user.id}`;
						if (!localStorage.getItem(promptedKey)) {
							setTimeout(async () => {
								try {
									const userId = session.user.id;
									const [{ count: itemCount }, { count: snapCount }] = await Promise.all([
										supabase
											.from('user_items')
											.select('id', { count: 'exact', head: true })
											.eq('user_id', userId),
										supabase
											.from('collectr_sync_snapshots')
											.select('id', { count: 'exact', head: true })
											.eq('user_id', userId)
									]);
									// Mark prompted regardless so we don't keep re-querying on every sign-in
									localStorage.setItem(promptedKey, '1');
									if ((itemCount ?? 0) > 0 || (snapCount ?? 0) > 0) return;

									toast('Have a Collectr export?', {
										description: 'Import your collection in seconds.',
										duration: 10000,
										action: {
											label: 'Import',
											onClick: () => {
												useUIStore.getState().requestCollectrImport();
												if (!window.location.pathname.startsWith('/inventory')) {
													navigate('/inventory');
												}
											}
										}
									});
								} catch (e) {
									console.error('[Auth] Failed to show import suggestion:', e);
								}
							}, 800);
						}
					}
				}
				
				// Sync email verification status to profiles table
				if (session.user.email_confirmed_at) {
					setTimeout(async () => {
						try {
							const { data: profile } = await supabase
								.from('profiles')
								.select('email_verified, verification_tier')
								.eq('user_id', session.user.id)
								.maybeSingle();
							
							if (profile && !profile.email_verified) {
								await supabase.from('profiles').update({
									email_verified: true,
									email_verified_at: session.user.email_confirmed_at,
									// Only upgrade tier if still at 'basic'
									...(profile.verification_tier === 'basic' && {
										verification_tier: 'email_verified'
									})
								}).eq('user_id', session.user.id);
								console.log('[Auth] Synced email verification status to profile');
							}
						} catch (error) {
							console.error('[Auth] Failed to sync email verification:', error);
						}
					}, 0);
				}

				// Note: pending role hints are claimed via the claim-early-role edge function
				// into the authoritative `user_roles` table. The legacy `profiles.user_role`
				// text column has been removed — never trust a role value from `profiles`.
				if (typeof window !== 'undefined') {
					sessionStorage.removeItem('pendingUserRole');
				}
			} else if (event === 'SIGNED_OUT') {
				// Purge react-query persisted cache so a different account signing
				// in on the same device never sees the previous user's inventory,
				// profile, or messaging state — even briefly.
				if (typeof window !== 'undefined') {
					try { window.localStorage.removeItem('cs.rq.cache.v1'); } catch {}
				}
				toast.info('You have been signed out');
			} else if (event === 'TOKEN_REFRESHED') {
				console.log('[Auth] Session refreshed');
			} else if (event === 'USER_UPDATED') {
				toast.info('Your profile was updated');
				
				// Check if email was just verified
				if (session?.user?.email_confirmed_at) {
					setTimeout(async () => {
						try {
							const { data: profile } = await supabase
								.from('profiles')
								.select('email_verified, verification_tier')
								.eq('user_id', session.user.id)
								.maybeSingle();
							
							if (profile && !profile.email_verified) {
								await supabase.from('profiles').update({
									email_verified: true,
									email_verified_at: session.user.email_confirmed_at,
									...(profile.verification_tier === 'basic' && {
										verification_tier: 'email_verified'
									})
								}).eq('user_id', session.user.id);
								console.log('[Auth] Synced email verification on USER_UPDATED');
							}
						} catch (error) {
							console.error('[Auth] Failed to sync email verification:', error);
						}
					}, 0);
				}
			}
		});

		return () => subscription.unsubscribe();
	}, []);

	const signUp = async (email: string, password: string) => {
		const normalizedEmail = email.toLowerCase().trim();
		const redirectUrl = `${window.location.origin}/auth/callback`;

		const { error } = await supabase.auth.signUp({
			email: normalizedEmail,
			password,
			options: {
				emailRedirectTo: redirectUrl
			}
		});

		return { error };
	};

	const signIn = async (email: string, password: string) => {
		const { error } = await supabase.auth.signInWithPassword({
			email,
			password
		});

		return { error };
	};

	// Sign in with email and password (or sign up if user doesn't exist)
	// Note: Password validation happens client-side and also by Supabase Auth server-side
	// CSRF protection is handled by Supabase Auth's PKCE flow
	// Note: Sessions persist in localStorage by default (Supabase client config)
	const signInOrSignUp = async (
		email: string,
		password: string,
		rememberMe: boolean = true,
		termsMetadata?: TermsMetadata,
		intentRole?: 'buyer' | 'seller'
	): Promise<{ error: any; didSignUp?: boolean }> => {
		const normalizedEmail = email.toLowerCase().trim();
		const redirectUrl = `${window.location.origin}/auth/callback`;

		// Best-effort early-role claim — never blocks the auth flow.
		// Edge fn is idempotent: no-ops if user already has a non-default role.
		const tryClaimEarlyRole = async () => {
			try {
				const { claimEarlyRole } = await import('@/hooks/useEarlyRole');
				await claimEarlyRole(intentRole);
			} catch (e) {
				console.error('[signInOrSignUp] claim-early-role failed', e);
			}
		};

		// First, try to sign in
		const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
			email: normalizedEmail,
			password
		});

		if (!signInError && signInData.user) {
			// Existing user - update general terms if provided and not already accepted
			if (termsMetadata?.generalTermsVersion) {
				await supabase
					.from('profiles')
					.update({
						general_terms_accepted_at: new Date().toISOString(),
						general_terms_version: termsMetadata.generalTermsVersion
					})
					.eq('user_id', signInData.user.id)
					.is('general_terms_accepted_at', null); // Only update if not already set
			}
			// Backfill early role for users who signed up via password before this fix
			await tryClaimEarlyRole();
			return { error: null, didSignUp: false };
		}

		// If "Invalid login credentials", try to sign up (account may not exist)
		if (signInError.message.includes('Invalid login credentials')) {
			const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
				email: normalizedEmail,
				password,
				options: {
					emailRedirectTo: redirectUrl,
					data: {
						...(termsMetadata?.generalTermsVersion
							? { general_terms_version: termsMetadata.generalTermsVersion }
							: {}),
						...(intentRole ? { early_role: intentRole } : {}),
					}
				}
			});

			if (!signUpError && signUpData.user) {
				// New user - set general terms acceptance in profile
				if (termsMetadata?.generalTermsVersion) {
					await supabase
						.from('profiles')
						.update({
							general_terms_accepted_at: new Date().toISOString(),
							general_terms_version: termsMetadata.generalTermsVersion
						})
						.eq('user_id', signUpData.user.id);
				}
				// Claim early role so signup gets early_buyer (or early_seller) and
				// becomes eligible for the gold-star badge.
				await tryClaimEarlyRole();
				return { error: null, didSignUp: true };
			}

			// Handle "User already registered" - means password was wrong
			if (signUpError.message.includes('User already registered')) {
				const friendlyError = new Error(
					"Incorrect password. Try again or use 'Forgot password?' to reset it."
				);
				return { error: friendlyError };
			}

			return { error: signUpError };
		}

		return { error: signInError };
	};

	const signInWithGoogle = async () => {
		try {
			const result = await lovable.auth.signInWithOAuth('google', {
				redirect_uri: window.location.origin,
			});
			if (result.error) {
				return { error: result.error };
			}
			return { error: null };
		} catch (error: any) {
			console.error('[Auth] Google sign-in failed', error);
			return { error };
		}
	};

	const signInWithApple = async () => {
		try {
			const result = await lovable.auth.signInWithOAuth('apple', {
				redirect_uri: window.location.origin,
			});
			if (result.error) {
				return { error: result.error };
			}
			return { error: null };
		} catch (error: any) {
			console.error('[Auth] Apple sign-in failed', error);
			return { error };
		}
	};

	const signInWithMagicLink = async (
		email: string,
		termsMetadata?: TermsMetadata,
		intentRole?: 'buyer' | 'seller'
	) => {
		const normalizedEmail = email.toLowerCase().trim();

		// Forward role intent through the callback URL so claim-early-role
		// can mint the right role after the magic-link round-trip.
		const callbackUrl = intentRole
			? `${window.location.origin}/auth/callback?role=${intentRole}`
			: `${window.location.origin}/auth/callback`;

		const otpData: Record<string, string> = {};
		if (termsMetadata?.generalTermsVersion) {
			otpData.general_terms_version = termsMetadata.generalTermsVersion;
		}
		if (intentRole) {
			// Tertiary fallback for claim-early-role's metadata path.
			otpData.early_role = intentRole;
		}

		const { error } = await supabase.auth.signInWithOtp({
			email: normalizedEmail,
			options: {
				emailRedirectTo: callbackUrl,
				data: Object.keys(otpData).length ? otpData : undefined
			}
		});

		if (!error) {
			toast.success(`Magic link sent to ${normalizedEmail}!`, {
				description: 'Check your inbox and click the link to sign in. Link expires in 1 hour.',
				duration: 10000
			});
		}

		return { error };
	};

	const signOut = async () => {
		await supabase.auth.signOut();
		setUser(null);
		setSession(null);
		hasShownWelcome.current = false;

		// Clear both collection and deck state when logging out
		useCardStore.getState().clearCollection();
		useDeckStore.getState().clearDeck();

		// Toast is shown by the SIGNED_OUT auth event handler (see above) — avoid double-toast.
		// Use window.location instead of navigate to avoid hook dependency
		window.location.href = '/';
	};

	const updatePassword = async (newPassword: string) => {
		const { error } = await supabase.auth.updateUser({
			password: newPassword
		});

		if (!error) {
			toast.success('Password updated successfully!');

			// Mark that user has a password
			if (user) {
				await supabase.from('profiles').update({ has_password: true }).eq('user_id', user.id);
			}
		}

		return { error };
	};

	const hasPassword = async (): Promise<boolean> => {
		if (!user) return false;

      const { data } = await supabase
        .from('profiles')
        .select('has_password')
        .eq('user_id', user.id)
        .maybeSingle();
      
      return data?.has_password ?? false;
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				session,
				loading,
				signUp,
				signIn,
				signInOrSignUp,
				signInWithGoogle,
				signInWithApple,
				signInWithMagicLink,
				signOut,
				updatePassword,
				hasPassword
			}}
		>
			{loading ? (
				<div className="flex items-center justify-center min-h-screen-safe bg-background" />
			) : (
				children
			)}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};
